// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { FrostError } from "./base";

/**
 * Thrown when attempting to use a wallet that hasn't been discovered.
 */
export class WalletNotFoundError extends FrostError {
  public readonly code = "WALLET_NOT_FOUND" as const;

  public constructor(walletName: string) {
    super(`Wallet "${walletName}" not found`, walletName);
  }
}

/**
 * Thrown when attempting an operation that requires a connected wallet.
 */
export class WalletDisconnectedError extends FrostError {
  public readonly code = "WALLET_DISCONNECTED" as const;

  public constructor() {
    super("No wallet connected");
  }
}

/**
 * Thrown when a wallet doesn't support the configured chain.
 */
export class UnsupportedChainError extends FrostError {
  public readonly code = "UNSUPPORTED_CHAIN" as const;
  public readonly chainId: string;
  public readonly supportedChains: readonly string[];

  public constructor(chainId: string, supportedChains: readonly string[], walletName: string) {
    super(
      `Wallet "${walletName}" does not support chain "${chainId}". ` +
        `Supported: ${supportedChains.join(", ")}`,
      walletName
    );
    this.chainId = chainId;
    this.supportedChains = supportedChains;
  }
}

/**
 * Thrown when a wallet doesn't support a required feature.
 */
export class UnsupportedFeatureError extends FrostError {
  public readonly code = "UNSUPPORTED_FEATURE" as const;
  public readonly feature: string;

  public constructor(feature: string, walletName: string) {
    super(`Wallet "${walletName}" does not support feature "${feature}"`, walletName);
    this.feature = feature;
  }
}

/**
 * Thrown when wallet connection fails.
 */
export class ConnectionFailedError extends FrostError {
  public readonly code = "CONNECTION_FAILED" as const;
  public readonly reason?: string;

  public constructor(reason: string, walletName: string, cause?: Error) {
    super(`Failed to connect to "${walletName}": ${reason}`, walletName, { cause });
    this.reason = reason;
  }
}

/**
 * Thrown when the persisted session has expired.
 */
export class SessionExpiredError extends FrostError {
  public readonly code = "SESSION_EXPIRED" as const;
  public readonly expiredAt: number;

  public constructor(expiredAt: number) {
    super("Session has expired");
    this.expiredAt = expiredAt;
  }
}

/**
 * Wrapper for errors thrown by the wallet itself.
 * We don't try to interpret these - just preserve the original error.
 */
export class WalletError extends FrostError {
  public readonly code = "WALLET_ERROR" as const;
  public readonly originalError: unknown;

  public constructor(originalError: unknown, walletName?: string) {
    const message =
      originalError instanceof Error ? originalError.message : "Wallet operation failed";

    super(message, walletName, {
      cause: originalError instanceof Error ? originalError : undefined,
    });
    this.originalError = originalError;
  }
}

/**
 * Thrown when a transaction is confirmed but execution failed on-chain.
 */
export class TransactionFailedError extends FrostError {
  public readonly code = "TRANSACTION_FAILED" as const;
  public readonly signature: string;
  public readonly reason?: string;

  public constructor(signature: string, reason?: string, walletName?: string) {
    super(reason ?? "Transaction execution failed on-chain", walletName);
    this.signature = signature;
    this.reason = reason;
  }
}

/** Union of all error codes */
export type FrostErrorCode =
  | "WALLET_NOT_FOUND"
  | "WALLET_DISCONNECTED"
  | "UNSUPPORTED_CHAIN"
  | "UNSUPPORTED_FEATURE"
  | "CONNECTION_FAILED"
  | "SESSION_EXPIRED"
  | "WALLET_ERROR"
  | "TRANSACTION_FAILED";
